import { useLocalization } from "@progress/kendo-react-intl";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessToken } from "../../../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { ListView, ListViewFooter, ListViewHeader, ListViewItemProps } from "@progress/kendo-react-listview";
import { SvgIcon } from "@progress/kendo-react-common";
import { Avatar } from "@progress/kendo-react-layout";
import { arrowRotateCwIcon, gearIcon, plusIcon, saveIcon, searchIcon, starIcon, trashIcon } from "@progress/kendo-svg-icons";
import { Switch, TextBox, TextBoxChangeEvent, TextBoxProps } from "@progress/kendo-react-inputs";
import { Dialog } from "@progress/kendo-react-dialogs";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { Button } from "@progress/kendo-react-buttons";
import { useLocation } from "react-router-dom";
import { loadingPanel } from "../../LoadingPanel";


/**
 * ConfigDevicesManagement is a component responsible for managing the configuration
 * of devices. It interacts with an API to fetch and manipulate device data, including
 * adding, updating, and disabling devices. The component maintains various states
 * for managing device lists, selected device details, loading states, and notifications.
 * It also utilizes localization services for displaying language-specific strings.
 */

export const ConfigDevicesManagement = () => {
    type SimulatedParameterType = 'boolean' | 'number' | 'datetime' | 'json' | 'text' | 'ipv4' | 'password';

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;

    const [devicesList, setDevicesList] = useState<any>(null);
    const [counterActiveDevices, setCounterActiveDevices] = useState(null);
    const [counterInactiveDevices, setCounterInactiveDevices] = useState(null);
    const [deviceToDisable, setDeviceToDisable] = useState<any>(null);
    const [deviceMacSelected, setDeviceMacSelected] = useState('');
    const [deviceTypeName, setDeviceTypeName] = useState('');
    const [deviceNameSelected, setDeviceNameSelected] = useState<TextBoxProps["value"]>('');
    const [deviceVersionSelected, setDeviceVersionSelected] = useState<TextBoxProps["value"]>('');

    const [batterySelected, setBatterySelected] = useState('');
    const [lastCommunicationTimeSelected, setLastCommunicationTimeSelected] = useState<any>('');
    const [mapIdSelected, setMapIdSelected] = useState<any>('');

    const [listViewData, setListViewData] = useState(devicesList);
    const [groupsList, setGroupsList] = useState<any>([]);
    const [groupNameSelected, setGroupNameSelected] = useState<any>(null);
    const [groupIdSelected, setGroupIdSelected] = useState<any>(null);

    const [jobsList, setJobsList] = useState<any>(null);
    const [jobSelected, setJobSelected] = useState<any>(null);
    const [currentJobSelected, setCurrentJobSelected] = useState<any>(null);
    const [jobIdSelected, setJobIdSelected] = useState<any>(null);
    const [jobsListName, setJobsListName] = useState<any>(null);

    const [messageDisabledDevice, setMessageDisabledDevice] = useState<boolean>(false);
    const [showPopupDisableDevice, setShowPopupDisableDevice] = useState<boolean>(false);

    const [messageAddedDevice, setMessageAddedDevice] = useState<boolean>(false);
    const [messageSavedDevice, setMessageSavedDevice] = useState<boolean>(false);
    const [messageUpdatingDevice, setMessageUpdatingDevice] = useState<boolean>(false);
    const [messageSimulatedConfigSaved, setMessageSimulatedConfigSaved] = useState<boolean>(false);
    const [emptyNameDevice, setEmptyNameDevice] = useState<boolean>(true);

    const [error, setError] = useState('');

    const [showPopupAddDevice, setShowPopupAddDevice] = useState<boolean>(false);
    const [showDeviceConfigModal, setShowDeviceConfigModal] = useState<boolean>(false);
    const [newDeviceMac, setNewDeviceMac] = useState<string>('');
    const [filterValue, setFilterValue] = useState("");
    const [selectedDevice, setSelectedDevice] = useState<any>(null);
    const [simulatedDeviceConfiguration, setSimulatedDeviceConfiguration] = useState<Array<{ group: string, key: string, label: string, value: any, type: SimulatedParameterType }>>([]);
    const [collapsedGroupCards, setCollapsedGroupCards] = useState<Record<string, boolean>>({});

    const localizationService = useLocalization();

    const [instance, accessTokenRequest] = AccessToken();

    let languageId = localStorage.getItem('languageId');

    const userType = localStorage.getItem('userType');


    const [loading, setLoading] = useState(false);
    const devicesListContainerRef = useRef<HTMLDivElement | null>(null);
    const listScrollTopRef = useRef(0);

    const location = useLocation();
    // get deviceMac from synoptic grid devices
    let deviceMac = location?.state?.deviceMac;

    const getListScrollElement = () => {
        const container = devicesListContainerRef.current;
        if (!container) {
            return null;
        }

        return (container.querySelector('.k-listview-content') as HTMLElement | null) ?? container;
    };

    const rememberListScrollPosition = () => {
        const scrollElement = getListScrollElement();
        if (!scrollElement) {
            return;
        }

        listScrollTopRef.current = scrollElement.scrollTop;
    };

    const restoreListScrollPosition = () => {
        const savedScrollTop = listScrollTopRef.current;

        const applyScroll = () => {
            const currentScrollElement = getListScrollElement();
            if (!currentScrollElement) {
                return;
            }

            currentScrollElement.scrollTop = savedScrollTop;
        };

        applyScroll();
        requestAnimationFrame(() => {
            applyScroll();

            // First selection also changes layout; apply twice to avoid initial jump-to-top.
            requestAnimationFrame(() => {
                applyScroll();
            });
        });
    };

    const getDeviceList = async (flag: boolean) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res: any) => {

                    if (res == '' || res?.data?.length == 0) {
                        setLoading(false);
                        return
                    }
                    if (res.length == 0) {
                        setLoading(false);
                        return
                    }

                    const data: any = res?.data
                    if (flag) {
                        setCounterActiveDevices(data?.data?.length);
                        setDevicesList(data?.data);
                    } else {
                        setCounterInactiveDevices(data?.data?.length);
                    }
                    setListViewData(data?.data);
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
    }, [])


    const onFilterChange = (ev: any) => {
        let value = ev.value;
        setFilterValue(value);
        const newData = devicesList.filter((device: any) => {
            return (
                device.name?.toLowerCase().includes(value.toLowerCase()) ||
                device.mac?.toLowerCase().includes(value.toLowerCase())
            );
        });
        setListViewData(newData);
    };

    const clearFilter = () => {
        setFilterValue("");          // azzera il campo input
        getDeviceList(true);
    };

    const getReadableLabelFromKey = (key: string) => {
        const expandedKey = key
            .replace(/AccTh/g, 'AccelerationThreshold')
            .replace(/Cnt/g, 'Count');

        return expandedKey
            .replace(/[_-]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^./, (char) => char.toUpperCase());
    };

    const getGroupDisplayName = (group: string) => {
        const groupNames: Record<string, string> = {
            MOT: 'Motion',
            NETW: 'Network',
            RELE: 'Relay',
            SENS: 'Sensors',
            SHCK: 'Shock',
            WIFI: 'WiFi'
        };

        return groupNames[group] ?? group;
    };

    const attachedSimulatedParameters: Array<{ group: string, parameter: string, value: string }> = [
        { group: 'MOT', parameter: 'HighAccTh', value: '1' },
        { group: 'MOT', parameter: 'HighSampleCnt', value: '200' },
        { group: 'MOT', parameter: 'LowAccTh', value: '1' },
        { group: 'MOT', parameter: 'LowSampleCnt', value: '3' },
        { group: 'NETW', parameter: 'ServerName', value: 'locator.customer.ubiquicom.com' },
        { group: 'NETW', parameter: 'ServerPort', value: '15000' },
        { group: 'RELE', parameter: 'ChecklistSlowdownReleChannel', value: '255' },
        { group: 'RELE', parameter: 'ChecklistSlowdownReleDefaultState', value: '0' },
        { group: 'RELE', parameter: 'EnablingReleChannel', value: '4' },
        { group: 'RELE', parameter: 'EnablingReleDefaultState', value: '0' },
        { group: 'RELE', parameter: 'ShockSpeedLimitRele', value: '0' },
        { group: 'RELE', parameter: 'ShockSpeedLimitReleChannel', value: '3' },
        { group: 'RELE', parameter: 'SlowdownReleChannel', value: '1' },
        { group: 'RELE', parameter: 'SlowdownReleDefaultState', value: '0' },
        { group: 'RELE', parameter: 'SpeedLimitReleChannel', value: '2' },
        { group: 'RELE', parameter: 'SpeedLimitReleDefaultState', value: '0' },
        { group: 'RELE', parameter: 'UwbZoningReleChannel', value: '0' },
        { group: 'RELE', parameter: 'UwbZoningReleDefaultState', value: '0' },
        { group: 'SENS', parameter: 'Analogic1_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Analogic1_Functionality', value: '' },
        { group: 'SENS', parameter: 'Analogic1_Label', value: '' },
        { group: 'SENS', parameter: 'Analogic1_Parameters', value: '' },
        { group: 'SENS', parameter: 'Analogic2_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Analogic2_Functionality', value: '' },
        { group: 'SENS', parameter: 'Analogic2_Label', value: '' },
        { group: 'SENS', parameter: 'Analogic2_Parameters', value: '' },
        { group: 'SENS', parameter: 'Analogic3_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Analogic3_Functionality', value: '' },
        { group: 'SENS', parameter: 'Analogic3_Label', value: '' },
        { group: 'SENS', parameter: 'Analogic3_Parameters', value: '' },
        { group: 'SENS', parameter: 'Analogic4_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Analogic4_Functionality', value: '' },
        { group: 'SENS', parameter: 'Analogic4_Label', value: '' },
        { group: 'SENS', parameter: 'Analogic4_Parameters', value: '' },
        { group: 'SENS', parameter: 'Digital1_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Digital1_Functionality', value: '' },
        { group: 'SENS', parameter: 'Digital1_Label', value: '' },
        { group: 'SENS', parameter: 'Digital1_Parameters', value: '' },
        { group: 'SENS', parameter: 'Digital2_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Digital2_Functionality', value: '' },
        { group: 'SENS', parameter: 'Digital2_Label', value: '' },
        { group: 'SENS', parameter: 'Digital2_Parameters', value: '' },
        { group: 'SENS', parameter: 'Digital3_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Digital3_Functionality', value: '' },
        { group: 'SENS', parameter: 'Digital3_Label', value: '' },
        { group: 'SENS', parameter: 'Digital3_Parameters', value: '' },
        { group: 'SENS', parameter: 'Digital4_Enabled', value: '0' },
        { group: 'SENS', parameter: 'Digital4_Functionality', value: '' },
        { group: 'SENS', parameter: 'Digital4_Label', value: '' },
        { group: 'SENS', parameter: 'Digital4_Parameters', value: '' },
        { group: 'SHCK', parameter: 'BuzzerOn', value: '1' },
        { group: 'SHCK', parameter: 'EscludedAxis', value: '0' },
        { group: 'SHCK', parameter: 'JerkThreshold', value: '300' },
        { group: 'SHCK', parameter: 'OffsetX', value: '0' },
        { group: 'SHCK', parameter: 'OffsetY', value: '0' },
        { group: 'SHCK', parameter: 'OffsetZ', value: '0' },
        { group: 'SHCK', parameter: 'Threshold', value: '32' },
        { group: 'SHCK', parameter: 'ThresholdWithJerk', value: '6' },
        { group: 'SHCK', parameter: 'Window', value: '10' },
        { group: 'WIFI', parameter: 'Dhcp', value: '1' },
        { group: 'WIFI', parameter: 'Password', value: 'ubiquicom' },
        { group: 'WIFI', parameter: 'Security', value: '2' },
        { group: 'WIFI', parameter: 'Ssid', value: 'trackvision' },
        { group: 'WIFI', parameter: 'TagDefaultGW', value: '192.168.51.1' },
        { group: 'WIFI', parameter: 'TagIpAddress', value: '192.168.51.11' },
        { group: 'WIFI', parameter: 'TagSubnetMask', value: '255.255.255.0' }
    ];

    const getSimulatedParameterType = (parameter: string): SimulatedParameterType => {
        const booleanParameters = new Set([
            'ChecklistSlowdownReleDefaultState',
            'EnablingReleDefaultState',
            'ShockSpeedLimitRele',
            'SlowdownReleDefaultState',
            'SpeedLimitReleDefaultState',
            'UwbZoningReleDefaultState',
            'BuzzerOn',
            'Dhcp'
        ]);

        if (booleanParameters.has(parameter) || parameter.endsWith('_Enabled')) {
            return 'boolean';
        }

        if (parameter === 'Password') {
            return 'password';
        }

        if (['TagDefaultGW', 'TagIpAddress', 'TagSubnetMask'].includes(parameter)) {
            return 'ipv4';
        }

        if ([
            'ServerName',
            'Ssid',
            'Analogic1_Functionality', 'Analogic1_Label', 'Analogic1_Parameters',
            'Analogic2_Functionality', 'Analogic2_Label', 'Analogic2_Parameters',
            'Analogic3_Functionality', 'Analogic3_Label', 'Analogic3_Parameters',
            'Analogic4_Functionality', 'Analogic4_Label', 'Analogic4_Parameters',
            'Digital1_Functionality', 'Digital1_Label', 'Digital1_Parameters',
            'Digital2_Functionality', 'Digital2_Label', 'Digital2_Parameters',
            'Digital3_Functionality', 'Digital3_Label', 'Digital3_Parameters',
            'Digital4_Functionality', 'Digital4_Label', 'Digital4_Parameters'
        ].includes(parameter)) {
            return 'text';
        }

        return 'number';
    };

    const normalizeValueByType = (type: SimulatedParameterType, value: string) => {
        if (type === 'boolean') {
            return value === '1' || value.toLowerCase() === 'true';
        }

        if (type === 'number') {
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? numericValue : 0;
        }

        return value ?? '';
    };

    const buildSimulatedConfigurationFromDevice = () => {
        return attachedSimulatedParameters.map((parameter) => {
            const type = getSimulatedParameterType(parameter.parameter);

            return {
                group: parameter.group,
                key: parameter.parameter,
                label: getReadableLabelFromKey(parameter.parameter),
                type,
                value: normalizeValueByType(type, parameter.value)
            };
        });
    };

    const openDeviceConfigurationModal = () => {
        const simulatedParameters = buildSimulatedConfigurationFromDevice();
        const collapsedByDefault = simulatedParameters.reduce((acc, parameter) => {
            acc[parameter.group] = true;
            return acc;
        }, {} as Record<string, boolean>);

        setSimulatedDeviceConfiguration(simulatedParameters);
        setCollapsedGroupCards(collapsedByDefault);
        setShowDeviceConfigModal(true);
    };

    const toggleGroupCard = (groupName: string) => {
        setCollapsedGroupCards((prev) => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    const handleSimulatedConfigurationChange = (parameterKey: string, value: any) => {
        setSimulatedDeviceConfiguration((prev) =>
            prev.map((parameter) =>
                parameter.key === parameterKey
                    ? { ...parameter, value }
                    : parameter
            )
        );
    };

    const saveSimulatedConfiguration = () => {
        setShowDeviceConfigModal(false);
        setMessageSimulatedConfigSaved(true);
        setTimeout(() => {
            setMessageSimulatedConfigSaved(false);
        }, 3000);
    };

    const groupedSimulatedConfiguration = simulatedDeviceConfiguration.reduce((acc, parameter) => {
        if (!acc[parameter.group]) {
            acc[parameter.group] = [];
        }
        acc[parameter.group].push(parameter);
        return acc;
    }, {} as Record<string, Array<{ group: string, key: string, label: string, value: any, type: SimulatedParameterType }>>);

    const MyHeader = () => {
        return (
            <>
                <ListViewHeader
                    style={{ color: "#018CA5", fontSize: 14 }}
                    className="pl-3 pb-2 pt-2"
                >
                    {localizationService.toLanguageString('configuration.devices.devices_list', 'NOT FOUND')} ({counterActiveDevices} {localizationService.toLanguageString('configuration.devices.active_devices', 'NOT_FOUND')})
                </ListViewHeader>
            </>
        );
    };

    const MyFooter = () => {
        let messages = 0;
        devicesList.map((i: any) => {
            messages = messages + i.messages;
        });
        return (
            <ListViewFooter
                style={{ color: "#018CA5", fontSize: 14 }}
                className="pl-3 pb-2 pt-2"
            >
            </ListViewFooter>
        );
    };

    const toggleDialog = (item?: any) => {
        setDeviceToDisable(item);
        setShowPopupDisableDevice(!showPopupDisableDevice);
    };

    const disableDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.put(`${SERVER_URL}/device/${deviceToDisable.mac}/false`, null, { headers })
                .then(() => {
                    getDeviceList(true)
                    setDeviceMacSelected('');
                    setMessageDisabledDevice(true);
                    if (!messageAddedDevice) {
                        setTimeout(() => {
                            setMessageDisabledDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        onToggleMessage()
    }

    // called on click device
    const getDeviceMacSelected = (device: any) => {
        rememberListScrollPosition();
        setSelectedDevice(device);
        setDeviceMacSelected(device.mac);
        setDeviceNameSelected(device.name);
        setDeviceTypeName(device.deviceTypeName);
        setDeviceVersionSelected(device.version);
        setGroupNameSelected(device.groupName);
        setGroupIdSelected(device.groupId);
        setJobIdSelected(device.jobId);

        setBatterySelected(device.battery);
        setLastCommunicationTimeSelected(device.lastCommunicationTime);
        setMapIdSelected(device.mapId);

        getGroupList();
        getJobsByGroup(device);
    }

    useEffect(() => {
        if (deviceMacSelected) {
            restoreListScrollPosition();
        }
    }, [deviceMacSelected]);

    const DevicesRender = (props: ListViewItemProps) => {
        let device = props.dataItem;
        return (
            <div
                className="k-listview-item row p-2 border-bottom align-middle"
                style={{ margin: 0, boxShadow: 'none' }}
            >
                <div
                    className="col-2"
                    style={{ 
                        alignContent: 'center',
                        pointerEvents: `${userType?.startsWith('UbiquicomAdmin') ? 'auto' : 'none'}`,
                        opacity: `${userType?.startsWith('UbiquicomAdmin') ? 1 : 0.5}`
                    }}
                    onClick={() => { toggleDialog(device) }}
                >
                    <Avatar
                        type="img"
                        style={{ cursor: 'pointer' }}
                        className="trash-icon"
                    >
                        <SvgIcon icon={trashIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                    </Avatar>
                </div>
                <div
                    className="col-9"
                    style={{ cursor: 'pointer', color: "#018CA5", }}
                    onClick={(e) => {
                        getDeviceMacSelected(device);
                        const title = e.currentTarget.querySelector('.device-management-name-h2') as HTMLElement | null;
                        title?.focus({ preventScroll: true });
                    }}
                >
                    <h2
                        className={`device-management-name-h2${deviceMacSelected === device.mac ? ' is-selected' : ''}`}
                        tabIndex={0}
                        style={{
                            fontSize: 16,
                            cursor: 'pointer',
                            padding: '10px'

                        }}
                    >
                        {device.name} ({device.mac})
                    </h2>
                </div>
            </div>
        );
    };

    const handleChangeDeviceName = useCallback((event: TextBoxChangeEvent) => {
        setDeviceNameSelected(event.target.value);
        setEmptyNameDevice(false);
    }, []);

    const getGroupList = async () => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/list?enabled=true`, { headers })
                .then((res) => {
                    const data = res.data.data

                    // add no group to the list
                    data.unshift({
                        id: '-1',
                        name: localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND')
                    })

                    setGroupsList(data);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getDeviceDetailsFromSynoptic = async (deviceMac: any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/device/active`, {
                headers
            })
                .then((res) => {
                    const data: any = res?.data.data

                    const currentDevice = data.filter((device: any) => device?.mac === deviceMac);
                    setSelectedDevice(currentDevice[0] ?? null);
                    setDeviceMacSelected(currentDevice[0].mac);
                    setDeviceNameSelected(currentDevice[0].name);
                    setDeviceTypeName(currentDevice[0].deviceTypeName);
                    setDeviceVersionSelected(currentDevice[0].version);
                    setGroupNameSelected(currentDevice[0].groupName);
                    setGroupIdSelected(currentDevice[0].groupId);
                    setBatterySelected(currentDevice[0].battery);
                    setLastCommunicationTimeSelected(currentDevice[0].lastCommunicationTime);
                    setMapIdSelected(currentDevice[0].mapId);

                    getJobsByGroupId(currentDevice[0].groupId);
                    getDeviceByMac(currentDevice[0].mac, true);
                    getGroupList();

                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getDeviceDetailsFromSynoptic(deviceMac);
    }, [deviceMac])

    useEffect(() => {
        getJobList(true);
    }, []);

    const getJobList = async (flag: boolean) => {

        setLoading(true);
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/job/list?enabled=${flag}`, { headers })
                .then((res) => {
                    const data = res?.data?.data
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    let jobList: any = []
                    data.forEach((job: any) => {
                        jobList.push(job)
                    });
                    setJobsList(jobList);
                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    };

    const getDeviceByMac = async (mac: any, active: any) => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.get(`${SERVER_URL}/Device/${mac}`, { headers })
                .then((res) => {
                    const data = res.data.data

                    if (data.groupId == 0) {
                        setGroupNameSelected(localizationService.toLanguageString('configuration.devices.no_group', 'NOT_FOUND'));
                    }

                    let selectedJob: any = {
                        id: data.jobId,
                        name: data.jobName
                    }
                    if (!data.jobId || !data.jobName) {
                        selectedJob = {
                            id: '-1',
                            name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND')
                        }
                    }
                    setJobSelected(selectedJob);
                    setCurrentJobSelected(selectedJob);
                    setJobIdSelected(selectedJob.id);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroup = async (device: any) => {

        if (device.groupId == -1 || device.groupId == 0) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${device.groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    setJobSelected([])
                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    getDeviceByMac(device.mac, true);

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const getJobsByGroupId = async (groupId: any, deviceMacSelected?: any) => {

        if (groupId == -1) {
            return;
        }

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/group/${groupId}/jobs`, { headers })
                .then((res) => {
                    const result = res.data.data
                    const data = result.filter((item: any) => item !== null);

                    data.unshift({ id: -1, name: localizationService.toLanguageString('configuration.devices.no_job', 'NOT_FOUND') })

                    setJobsListName(data)

                    setLoading(false);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                    setLoading(false);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const handleChangeGroup = (group: any) => {
        setGroupIdSelected(group.id);
        setGroupNameSelected(group.name);

        getJobsByGroupId(group.id, deviceMacSelected);
        setJobSelected(-1)
        setJobIdSelected(-1)
    }


    const handleChangeJob = useCallback((event: DropDownListChangeEvent, jobsListName: any) => {
        const currentJobSelected = event.value
        setJobSelected(currentJobSelected);
        setJobIdSelected(currentJobSelected.id)
    }, [])

    const CustomTitle = () => {
        return (
            <div
                className="custom-title"
                style={{ fontSize: "18px", lineHeight: "1.3em" }}
            >
                {localizationService.toLanguageString('configuration.devices.disable_device', 'NOT FOUND')} "{deviceToDisable.name} ({deviceToDisable.mac})" ?
            </div>
        );
    };

    const onToggleMessage = () => {
        setMessageDisabledDevice(true);
        if (!messageDisabledDevice) {
            setTimeout(() => {
                setMessageDisabledDevice(false);
            }, 3000);
        }
    };

    const openPopupAddDevice = () => {
        setShowPopupAddDevice(!showPopupAddDevice);
    }

    const addDevice = async () => {

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}` };
            axios.post(`${SERVER_URL}/device/${newDeviceMac}`, null, { headers })
                .then(() => {
                    setDeviceNameSelected('');
                    setDeviceVersionSelected('');
                    setGroupNameSelected('');
                    setGroupIdSelected('');
                    setMessageAddedDevice(true);
                    getDeviceList(true)
                    getGroupList()
                    setDeviceMacSelected(newDeviceMac)
                    

                    setMessageSavedDevice(true);
                    if (!messageSavedDevice) {
                        setTimeout(() => {
                            setMessageSavedDevice(false);
                        }, 3000);
                    }
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }

        if (!messageAddedDevice) {
            setTimeout(() => {
                setMessageAddedDevice(false);
            }, 3000);
        }

    }

    const updateDevice = async () => {

        setLoading(true);

        const normalizedGroupId = Number(groupIdSelected);
        const normalizedJobId = Number(jobIdSelected);
        const fallbackJobId = Number(jobSelected?.id ?? currentJobSelected?.id);

        const resolvedJobId = Number.isFinite(normalizedJobId)
            ? normalizedJobId
            : Number.isFinite(fallbackJobId)
                ? fallbackJobId
                : null;

        const params: any = {
            name: deviceNameSelected,
            version: deviceVersionSelected,
            groupId: normalizedGroupId > 0 ? normalizedGroupId : null,
        };

        if (normalizedGroupId === -1) {
            params.jobId = null;
        } else if (resolvedJobId !== null && resolvedJobId > 0) {
            params.jobId = resolvedJobId;
        } else if (resolvedJobId === -1) {
            params.jobId = null;
        }


        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            try {
                await axios.put(`${SERVER_URL}/device/${deviceMacSelected}`, params, { headers })
                setMessageUpdatingDevice(true);

                clearFilter()

                if (!messageUpdatingDevice) {
                    setTimeout(() => {
                        setMessageUpdatingDevice(false);
                    }, 3000);
                }

                setLoading(false);
            } catch (error: any) {
                setError(error);
                setLoading(false);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }

    }

    return (
        <>
            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == null &&
                // Mostra un messaggio o un altro contenuto se non ci sono dati
                <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            {devicesList?.length > 0 &&
                <div className="flex-parent-element" 
                    style={{overflow: 'hidden'}}    
                >

                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {messageDisabledDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageDisabledDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.device_disabled', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageAddedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageAddedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.added_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageSavedDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageSavedDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.saved_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageUpdatingDevice && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageUpdatingDevice(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.devices.updated_device', 'NOT FOUND')}</span>
                                </Notification>
                            )}
                            {messageSimulatedConfigSaved && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageSimulatedConfigSaved(false)}
                                >
                                    <span>Configuration saved</span>
                                </Notification>
                            )}
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div
                        className="flex-left-child-element"
                        ref={devicesListContainerRef}
                        style={{ width: "42%", background: "#F7F7F7" }}
                    >
                        {loading && loadingPanel}
                        <div
                            className="k-listview-item row p-2 align-middle"
                            style={{ display: 'block', margin: 0 }}
                        >
                            <fieldset style={{ display: 'flex' }} >
                                <TextBox
                                    style={{
                                        border: '2px solid #ccc',
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    value={filterValue}
                                    onChange={onFilterChange}
                                    placeholder={localizationService.toLanguageString('configuration.devices.search_device', 'NOT FOUND')}
                                    prefix={() => (
                                        <SvgIcon icon={searchIcon} svgStyle={{ margin: '20px -5px 0px 5px' }} />
                                    )}
                                />

                                {/* refresh device list button */}
                                <Button
                                    className="plusParam"
                                    style={{ marginTop: '0px' }}
                                    onClick={() => { getDeviceList(true) }}
                                    title={localizationService.toLanguageString('configuration.devices.refresh_list', 'NOT FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon icon={arrowRotateCwIcon} style={{ margin: '8px' }} />
                                    </Avatar>
                                </Button>

                                {/* add device button */}
                                <Button
                                    themeColor={"primary"}
                                    className="plusParam"
                                    onClick={() => { openPopupAddDevice() }}
                                    style={{ marginTop: '0px' }}
                                    title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT FOUND')}
                                >
                                    <Avatar
                                        className="plusParam"
                                    >
                                        <SvgIcon
                                            icon={plusIcon}
                                            style={{
                                                margin: '8px'
                                            }}
                                        />
                                    </Avatar>
                                </Button>
                            </fieldset>
                        </div>

                        <ListView
                            data={listViewData ?? devicesList}
                            item={DevicesRender}
                            style={{ background: "#F7F7F7", height: '90%' }}
                            header={MyHeader}
                            footer={MyFooter}
                        />
                        {showPopupAddDevice && (
                            <Dialog
                                title={localizationService.toLanguageString('configuration.devices.add_device', 'NOT FOUND')}
                                onClose={openPopupAddDevice}
                                contentStyle={{ color: 'black' }}
                            >
                                <TextBox
                                    style={{
                                        boxShadow: 'inset 0px 0px 5px 0px rgba(0,0,0,0.13)',
                                        marginBottom: '10px',
                                    }}
                                    onChange={(e: any) => setNewDeviceMac(e.target.value)}
                                    placeholder={localizationService.toLanguageString('configuration.devices.mac', 'NOT FOUND')}
                                />
                                <div className='k-window-content k-buttons-container' >
                                    <button
                                        className="add-device-button"
                                        onClick={openPopupAddDevice}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT FOUND')}
                                    </button>
                                    <button
                                        className="add-device-button"
                                        onClick={() => {
                                            if (!showPopupDisableDevice) {
                                                openPopupAddDevice()
                                                addDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                        {showPopupDisableDevice && (
                            <Dialog
                                title={<CustomTitle />}
                                onClose={toggleDialog}
                            >
                                <div>
                                    <button
                                        className="disable-device-button"
                                        onClick={toggleDialog}
                                    >
                                        {localizationService.toLanguageString('no', 'NOT FOUND')}
                                    </button>
                                    <button
                                        className="disable-device-button"
                                        onClick={() => {
                                            if (showPopupDisableDevice) {
                                                toggleDialog()
                                                disableDevice()
                                            }
                                        }}
                                    >
                                        {localizationService.toLanguageString('yes', 'NOT FOUND')}
                                    </button>
                                </div>
                            </Dialog>
                        )}
                        {showDeviceConfigModal && (
                            <Dialog
                                title="Configure device"
                                onClose={() => setShowDeviceConfigModal(false)}
                            >
                                <div style={{ maxHeight: '55vh', overflowY: 'auto', minWidth: '520px' }}>
                                    {simulatedDeviceConfiguration.length === 0 && (
                                        <p>No configuration parameters available</p>
                                    )}
                                    {Object.entries(groupedSimulatedConfiguration).map(([groupName, groupParameters]) => (
                                        <div
                                            key={groupName}
                                            style={{
                                                border: '1px solid #d9d9d9',
                                                borderRadius: '8px',
                                                padding: '12px',
                                                marginBottom: '14px',
                                                backgroundColor: '#fafafa'
                                            }}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => toggleGroupCard(groupName)}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                    outline: 'none',
                                                    boxShadow: 'none',
                                                    marginBottom: collapsedGroupCards[groupName] ? 0 : '10px'
                                                }}
                                            >
                                                <span className="k-h5" style={{ color: '#018CA5', margin: 0 }}>
                                                    {getGroupDisplayName(groupName)}
                                                </span>
                                                <span style={{ color: '#018CA5', fontSize: '18px', lineHeight: 1 }}>
                                                    {collapsedGroupCards[groupName] ? '+' : '-'}
                                                </span>
                                            </button>
                                            {!collapsedGroupCards[groupName] && groupParameters.map((parameter) => (
                                                <div key={`${parameter.group}-${parameter.key}`} style={{ marginBottom: '12px' }}>
                                                    <div className="k-h6" style={{ marginBottom: '4px', color: '#018CA5' }}>
                                                        {parameter.label}
                                                    </div>
                                                    {parameter.type === 'boolean' && (
                                                        <Switch
                                                            checked={Boolean(parameter.value)}
                                                            onChange={(event: any) => handleSimulatedConfigurationChange(parameter.key, Boolean(event.value))}
                                                        />
                                                    )}
                                                    {parameter.type === 'number' && (
                                                        <input
                                                            className="k-input k-textbox"
                                                            type="number"
                                                            inputMode="numeric"
                                                            style={{ width: '100%', minHeight: '34px', borderRadius: '4px', padding: '6px 8px' }}
                                                            value={String(parameter.value ?? '')}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                handleSimulatedConfigurationChange(parameter.key, value === '' ? '' : Number(value));
                                                            }}
                                                        />
                                                    )}
                                                    {parameter.type === 'datetime' && (
                                                        <input
                                                            className="k-input k-textbox"
                                                            type="datetime-local"
                                                            style={{ width: '100%', minHeight: '34px', borderRadius: '4px', padding: '6px 8px' }}
                                                            value={String(parameter.value ?? '')}
                                                            onChange={(event) => handleSimulatedConfigurationChange(parameter.key, event.target.value)}
                                                        />
                                                    )}
                                                    {parameter.type === 'json' && (
                                                        <textarea
                                                            className="k-textarea"
                                                            style={{ width: '100%', minHeight: '110px', borderColor: '#d8d8d8', borderRadius: '4px', padding: '8px' }}
                                                            value={String(parameter.value ?? '')}
                                                            onChange={(event) => handleSimulatedConfigurationChange(parameter.key, event.target.value)}
                                                        />
                                                    )}
                                                    {parameter.type === 'password' && (
                                                        <TextBox
                                                            type="password"
                                                            value={parameter.value ?? ''}
                                                            onChange={(event: TextBoxChangeEvent) => handleSimulatedConfigurationChange(parameter.key, String(event.target.value ?? ''))}
                                                        />
                                                    )}
                                                    {parameter.type === 'ipv4' && (
                                                        <TextBox
                                                            value={parameter.value ?? ''}
                                                            onChange={(event: TextBoxChangeEvent) => handleSimulatedConfigurationChange(parameter.key, String(event.target.value ?? ''))}
                                                            valid={/^((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/.test(String(parameter.value ?? ''))}
                                                        />
                                                    )}
                                                    {parameter.type === 'text' && (
                                                        <TextBox
                                                            value={parameter.value ?? ''}
                                                            onChange={(event: TextBoxChangeEvent) => handleSimulatedConfigurationChange(parameter.key, String(event.target.value ?? ''))}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                    <Button onClick={() => setShowDeviceConfigModal(false)}>
                                        Close
                                    </Button>
                                    <Button
                                        themeColor={"primary"}
                                        onClick={saveSimulatedConfiguration}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Dialog>
                        )}
                    </div>

                    {/* right panel */}
                    {deviceMacSelected != '' &&
                        <div
                            style={{ 
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                                alignItems: 'center'
                            }}
                        >
                            {/* mac + name */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <h1 className="k-h2" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.mac', 'NOT_FOUND')}</h1>
                                    <h5 className="k-h5" style={{ color: '#018CA5' }}>{deviceMacSelected != '' ? deviceMacSelected : localizationService.toLanguageString('configuration.devices.no_mac', 'NOT_FOUND')}</h5>

                                    <hr style={{ border: '1px solid #018CA5', width: '100%' }} />

                                    <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.name', 'NOT_FOUND')}</div>
                                    <TextBox
                                        value={deviceNameSelected ?? ''}
                                        onChange={handleChangeDeviceName}
                                        placeholder={localizationService.toLanguageString('configuration.devices.enter_device_name', 'NOT_FOUND')}
                                    />
                                </fieldset>
                            </div>

                            <br />

                            {/* type */}
                            <div className="k-listview-item row p-1 align-middle">
                                <div className="col-2" >
                                    <Avatar type="img" style={{ background: "#018CA5" }}>
                                        <SvgIcon icon={starIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                                    </Avatar>
                                </div>
                                <div className="col-9" >
                                    <h1
                                        style={{
                                            fontSize: 16,
                                            color: "#018CA5",
                                            verticalAlign: 'bottom',
                                            display: 'inline-flex'
                                        }}
                                        className="text-uppercase"
                                    >
                                        {deviceTypeName != '' ? deviceTypeName : ''}
                                    </h1>
                                </div>
                            </div>

                            <br />

                            {/* version */}
                            {deviceVersionSelected != '' &&
                                <div
                                    className="k-listview-item row p-1 align-middle"
                                    style={{ display: 'block', margin: 0, cursor: 'default' }}
                                >
                                    <fieldset>
                                        <div className="k-h5" style={{ display: 'flex', alignItems: 'baseline', color: '#018CA5' }}>
                                            <span style={{ marginRight: '10px'}}>{localizationService.toLanguageString('configuration.devices.version', 'NOT_FOUND')}:</span>
                                            <p>{deviceVersionSelected ?? ''}</p>
                                        </div>                                        
                                    </fieldset>
                                </div>
                            }
                           
                            {/* groups */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                <fieldset>
                                    <div className="k-h5" style={{ color: '#018CA5', marginTop: '10px' }}>{localizationService.toLanguageString('configuration.devices.groups', 'NOT_FOUND')}</div>
                                    <DropDownList
                                        data={groupsList}
                                        textField="name"
                                        dataItemKey="id"
                                        value={groupsList.find((g:any) => g.id === groupIdSelected) || null}
                                        onChange={(e:any) => handleChangeGroup(e.value)}
                                        style={{ width: "100%", color: '#018CA5', cursor: 'pointer' }}
                                    />
                                </fieldset>
                            </div>

                            {/* jobs */}
                            <div
                                className="k-listview-item row p-1 align-middle"
                                style={{ display: 'block', margin: 0 }}
                            >
                                {groupIdSelected != 0 && groupIdSelected != -1 && jobsListName?.length > 0 &&
                                    <fieldset>
                                        <div className="k-h5" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('configuration.devices.jobs', 'NOT_FOUND')}</div>
                                        <DropDownList
                                            data={jobsListName ?? []}
                                            dataItemKey="id"
                                            defaultValue={jobsListName[0]}
                                            value={!jobSelected?.name ? jobsListName[0] : jobSelected}
                                            textField={'name'}
                                            onChange={(e: any) => handleChangeJob(e, jobsListName)}
                                            style={{ width: "100%", color: '#018CA5', cursor: 'pointer' }}
                                        />
                                    </fieldset>
                                }

                                {/*  save button */}
                                <div className="k-listview-item row p-3">
                                    <Button
                                        style={{ marginRight: '10px' }}
                                        svgIcon={gearIcon}
                                        onClick={openDeviceConfigurationModal}
                                    >
                                        Configure
                                    </Button>
                                    <Button
                                        className="save-devices-management-button"
                                        themeColor={"primary"}
                                        svgIcon={saveIcon}
                                        onClick={() => updateDevice()}
                                    >
                                        {localizationService.toLanguageString('configuration.devices.save', 'NOT_FOUND')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }
        </>
    )
}